import React, { useState, useEffect, useCallback } from 'react'
import { Button, InputNumber, message, Row, Switch, Table } from 'antd'

import {ServiceProviderLicense} from '../../../types'
import { serviceProviderService } from '../../../services'


interface LicenseLimitProps {
	id: number
	isMondagoAdmin: boolean
}

const IS_LIMIT_ENABLED_KEY = 'is_limit_enabled'
const LIMIT_KEY = 'limit'

const LicenseLimits = ({ id, isMondagoAdmin }: LicenseLimitProps) => {
	const [licenses, setLicenses] = useState<Record<string, ServiceProviderLicense>>()
	const [changes, setChanges] = useState<Record<string, Partial<ServiceProviderLicense>>>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSaving, setIsSaving] = useState<boolean>(false)

	const getLicenses = useCallback(async () => {
		try {
			setIsLoading(true)
			const { data } = await serviceProviderService.getLicenses(id)
			return data
		} catch (error) {
			message.error('There was an unexpected problem when attempting to retrieve the licenses. Please try again later.')
			return {}
		} finally {
			setIsLoading(false)
		}
	}, [setIsLoading])

	const resetChanges = async () => {
		setLicenses(await getLicenses())
		setChanges(null)
	}

	const saveChanges = async () => {
		const changesToProcess = Object.entries(changes).map(([type, payload]) => serviceProviderService.updateLicense(id, type, payload))

		try {
			setIsSaving(true)
			await Promise.all(changesToProcess)
			message.success('Licenses succesfully updated')
		} catch {
			message.error('There was an unexpected error when attempting to update the licenses. Please check the values and try again.')
		}

		setIsSaving(false)
		await resetChanges()
	}

	const handleChange = (property: keyof ServiceProviderLicense, value: number | boolean, id: number) => {
		setChanges(prev => {
			const data = Object.assign({}, prev)

			if (data[id]) {
				data[id] = {
					...data[id],
					[property]: value
				}
				return data
			}

			return {
				...data,
				[id]: {
					[property]: value
				}
			}
		})
	}

	useEffect(() => {
		getLicenses().then(setLicenses)
	}, [getLicenses, setLicenses])

	const handleAllocationLimitBlur = (allocated: number, id: number) => {
		const limit = changes?.[id]?.[LIMIT_KEY]

		if (limit < allocated) {
			message.warn(`The limit cannot be less than the allocated licenses. The limit has been set to ${allocated}.`)
			handleChange(LIMIT_KEY, allocated, id)
		}
	}

	return licenses ? (
		<>
			<Row>
				<Table rowKey="id" loading={isLoading || isSaving} dataSource={Object.values(licenses)} pagination={false}>
					<Table.Column title='License Type' dataIndex='display_name' />
					<Table.Column
						title='Limit Enabled'
						dataIndex={IS_LIMIT_ENABLED_KEY}
						render={(enabled, { id }: ServiceProviderLicense) => (
							<Switch
								disabled={!isMondagoAdmin}
								defaultChecked={enabled}
								onChange={enabled => handleChange(IS_LIMIT_ENABLED_KEY, enabled, id)}
								checked={changes?.[id]?.[IS_LIMIT_ENABLED_KEY] ?? enabled}
							/>
						)}
						align='center'
					/>
					<Table.Column
						title='Allocation Limit'
						dataIndex={LIMIT_KEY}
						render={(limit: number, { is_limit_enabled, allocated, id }: ServiceProviderLicense) => {
							const isLimitEnabledFromChanges = changes?.[id]?.[IS_LIMIT_ENABLED_KEY]
							return isMondagoAdmin ? (
								<InputNumber
									disabled={isLimitEnabledFromChanges === undefined ? !is_limit_enabled : !isLimitEnabledFromChanges}
									min={allocated}
									defaultValue={limit}
									value={changes?.[id]?.[LIMIT_KEY] ?? limit}
									onChange={value => handleChange(LIMIT_KEY, value, id)}
									onBlur={() => handleAllocationLimitBlur(allocated, id)}
								/>
							) : (
								<span>{limit}</span>
							)
						}}
						align='center'
					/>
					<Table.Column title='Allocated' dataIndex='allocated' align='center' />
					<Table.Column title='Consumed' dataIndex='consumed' align='center' />
				</Table>
			</Row>
			{changes && (
				<Row justify='end' type='flex' style={{ marginTop: '1rem' }}>
					<Button disabled={isLoading || isSaving} style={{ marginRight: '.5rem' }} onClick={() => resetChanges()}>
						Reset
					</Button>
					<Button disabled={isLoading} loading={isSaving} type='primary' onClick={() => saveChanges()}>
						Save Changes
					</Button>
				</Row>
			)}
		</>
	) : null
}

export default LicenseLimits
